import { useCallback, useEffect, useMemo, useState } from "react";
import en from "../locales/en";
import {
  DEFAULT_EXCHANGE_RATES,
  defaultCategoryBudgets,
  emptyFilters
} from "../constants";
import { expenseService } from "../services/expenseService";
import { storageService } from "../services/storageService";
import {
  enrichExpenses,
  filterExpenses,
  getCategoryBudgetData,
  getCategoryData,
  getMonthlyData,
  getMonthlyInsights,
  materializeRecurringExpenses
} from "../utils/finance";
import { createId, downloadCsv, downloadJson } from "../utils/formatters";

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const sortByDateDesc = (items) =>
  [...items].sort((left, right) => new Date(right.date) - new Date(left.date));

const noop = () => {};

export default function useExpenses(currentUser, filters = emptyFilters, options = {}) {
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const showNotification = options.showNotification || noop;
  const locale = options.locale || en;
  const messages = locale.notifications;
  const notifyText = useCallback(
    (key, ...args) => {
      const value = messages[key] || en.notifications[key];
      return typeof value === "function" ? value(...args) : value;
    },
    [messages]
  );

  const currentUserId = currentUser?.id;
  const currencyCode = currentUser?.currency?.value || "USD";
  const exchangeRates = currentUser?.exchangeRates || DEFAULT_EXCHANGE_RATES;
  const categoryBudgets = currentUser?.categoryBudgets || defaultCategoryBudgets;
  const monthlyBudget = Number(currentUser?.monthlyBudget || 0);
  const monthlyIncome = Number(currentUser?.monthlyIncome || 0);

  const persistExpenses = useCallback(
    async (nextExpenses, successMessage) => {
      if (!currentUserId) return [];

      const recurringResult = materializeRecurringExpenses(nextExpenses, currentUserId);
      await storageService.saveExpenses(currentUserId, recurringResult.expenses);
      setExpenses(recurringResult.expenses);

      if (successMessage) {
        showNotification("success", successMessage);
      }

      return recurringResult.expenses;
    },
    [currentUserId, showNotification]
  );

  const loadExpenses = useCallback(async () => {
    if (!currentUserId) {
      setExpenses([]);
      return;
    }

    setIsLoading(true);

    try {
      const storedExpenses = await storageService.getExpenses(currentUserId);
      const recurringResult = materializeRecurringExpenses(storedExpenses, currentUserId);

      if (recurringResult.generatedCount) {
        await storageService.saveExpenses(currentUserId, recurringResult.expenses);
        showNotification(
          "info",
          notifyText(
            "recurringExpensesAdded",
            recurringResult.generatedCount
          )
        );
      }

      setExpenses(recurringResult.expenses);
    } catch (error) {
      showNotification("error", error.message || notifyText("expensesLoadFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, notifyText, showNotification]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  const addExpense = useCallback(
    async (draft) => {
      if (!currentUserId) return;

      setIsLoading(true);

      try {
        const payload = {
          ...draft,
          id: createId(),
          amount: Number(draft.amount),
          currency: draft.currency || currencyCode,
          date: (draft.date instanceof Date ? draft.date : new Date(draft.date)).toISOString(),
          userId: currentUserId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await persistExpenses([payload, ...expenses], notifyText("expenseAdded"));
      } catch (error) {
        showNotification("error", error.message || notifyText("expenseSaveFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [currencyCode, currentUserId, expenses, notifyText, persistExpenses, showNotification]
  );

  const updateExpense = useCallback(
    async (expense, draft) => {
      if (!currentUserId || !expense) return;

      setIsLoading(true);

      try {
        const payload = {
          ...draft,
          id: expense.id,
          amount: Number(draft.amount),
          currency: draft.currency || currencyCode,
          date: (draft.date instanceof Date ? draft.date : new Date(draft.date)).toISOString(),
          userId: currentUserId,
          createdAt: expense.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        const nextExpenses = expenses.map((item) => (item.id === expense.id ? payload : item));

        await persistExpenses(nextExpenses, notifyText("expenseUpdated"));
      } catch (error) {
        showNotification("error", error.message || notifyText("expenseUpdateFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [currencyCode, currentUserId, expenses, notifyText, persistExpenses, showNotification]
  );

  const restoreExpense = useCallback(
    async (removedExpenses, baseExpenses = expenses) => {
      setIsLoading(true);

      try {
        const restoredExpenses = sortByDateDesc([...removedExpenses, ...baseExpenses]);
        await persistExpenses(restoredExpenses, notifyText("expenseRestored"));
      } catch (error) {
        showNotification("error", error.message || notifyText("expenseRestoreFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [expenses, notifyText, persistExpenses, showNotification]
  );

  const deleteExpense = useCallback(
    async (expenseId) => {
      setIsLoading(true);

      try {
        const removedExpenses = expenses.filter(
          (expense) => expense.id === expenseId || expense.parentRecurringId === expenseId
        );

        if (!removedExpenses.length) return;

        const nextExpenses = expenses.filter(
          (expense) => expense.id !== expenseId && expense.parentRecurringId !== expenseId
        );

        await persistExpenses(nextExpenses);
        showNotification("success", notifyText("expenseDeleted"), {
          actionLabel: notifyText("undo"),
          duration: 5000,
          onAction: () => restoreExpense(removedExpenses, nextExpenses)
        });
      } catch (error) {
        showNotification("error", error.message || notifyText("expenseDeleteFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [expenses, notifyText, persistExpenses, restoreExpense, showNotification]
  );

  const saveExpense = useCallback(
    (draft, editingExpense) =>
      editingExpense ? updateExpense(editingExpense, draft) : addExpense(draft),
    [addExpense, updateExpense]
  );

  const importExpenses = useCallback(
    async (file) => {
      if (!currentUserId) return;

      setIsLoading(true);

      try {
        const importedExpenses = await expenseService.importFromFile(file, currentUserId, currencyCode);

        if (!importedExpenses.length) {
          showNotification("warning", notifyText("importEmpty"));
          return;
        }

        await persistExpenses(
          [...importedExpenses, ...expenses],
          notifyText("expensesImported", importedExpenses.length)
        );
      } catch (error) {
        showNotification("error", error.message || notifyText("importFailed"));
      } finally {
        setIsLoading(false);
      }
    },
    [currencyCode, currentUserId, expenses, notifyText, persistExpenses, showNotification]
  );

  const resetExpenses = useCallback(async () => {
    if (!currentUserId) return;

    setIsLoading(true);

    try {
      await storageService.saveExpenses(currentUserId, []);
      setExpenses([]);
    } catch (error) {
      showNotification("error", error.message || notifyText("expensesResetFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [currentUserId, notifyText, showNotification]);

  const replaceExpenses = useCallback((nextExpenses) => {
    setExpenses(nextExpenses);
  }, []);

  const enrichedExpenses = useMemo(
    () => enrichExpenses(expenses, currencyCode, exchangeRates),
    [expenses, currencyCode, exchangeRates]
  );

  const displayedExpenses = useMemo(
    () => filterExpenses(enrichedExpenses, filters),
    [enrichedExpenses, filters]
  );

  const currentMonthExpenses = useMemo(
    () => enrichedExpenses.filter((expense) => expense.monthKey === getCurrentMonthKey()),
    [enrichedExpenses]
  );

  const recentExpenses = useMemo(
    () => sortByDateDesc(currentMonthExpenses).slice(0, 5),
    [currentMonthExpenses]
  );

  const total = useMemo(
    () => currentMonthExpenses.reduce((sum, expense) => sum + Number(expense.amountConverted || 0), 0),
    [currentMonthExpenses]
  );
  const balance = monthlyIncome - total;
  const remainingBudget = Math.max(0, monthlyBudget - total);
  const budgetPercentage = monthlyBudget > 0 ? Math.min(100, (total / monthlyBudget) * 100) : 0;
  const recurringCount = currentMonthExpenses.filter(
    (expense) => expense.frequency !== "One-time" || expense.parentRecurringId
  ).length;

  const categoryData = useMemo(() => getCategoryData(currentMonthExpenses), [currentMonthExpenses]);
  const monthlyData = useMemo(
    () => getMonthlyData(enrichedExpenses, locale.localeCode),
    [enrichedExpenses, locale.localeCode]
  );
  const categoryBudgetData = useMemo(
    () => getCategoryBudgetData(currentMonthExpenses, categoryBudgets),
    [currentMonthExpenses, categoryBudgets]
  );
  const insights = useMemo(
    () => getMonthlyInsights(enrichedExpenses, monthlyBudget, categoryBudgets, locale),
    [enrichedExpenses, monthlyBudget, categoryBudgets, locale]
  );

  const exportCsv = useCallback(
    () => {
      downloadCsv(expenseService.exportRows(displayedExpenses), `ledgerflow-expenses-${new Date().toISOString().slice(0, 10)}.csv`);
      showNotification("success", notifyText("csvExported"));
    },
    [displayedExpenses, notifyText, showNotification]
  );

  const exportJson = useCallback(
    () => {
      downloadJson(expenseService.exportRows(displayedExpenses), `ledgerflow-expenses-${new Date().toISOString().slice(0, 10)}.json`);
      showNotification("success", notifyText("jsonExported"));
    },
    [displayedExpenses, notifyText, showNotification]
  );

  return {
    addExpense,
    balance,
    budgetPercentage,
    categoryBudgetData,
    categoryData,
    currentMonthExpenses,
    deleteExpense,
    displayedExpenses,
    enrichedExpenses,
    expenses,
    exportCsv,
    exportJson,
    importExpenses,
    insights,
    isLoading,
    monthlyData,
    recentExpenses,
    recurringCount,
    remainingBudget,
    replaceExpenses,
    resetExpenses,
    restoreExpense,
    saveExpense,
    total,
    updateExpense
  };
}
